// «Mis peticiones» en el panel de lealtad (F2 del sistema de peticiones, 11-oct-2026). Lo que el negocio le
// ha pedido a Lealify (arreglos, mejoras, dudas) con número, estado y lo que se hizo. Misma API que la
// agenda dental: lealify-base/backend/routes/client-requests.js. El negocio LEE, CREA y RESPONDE; los
// cambios de estado los hace Lealify (forobeta-ops/scripts/peticion.py). Crear/responder avisa a Carlos
// por WhatsApp, por eso la pantalla no reintenta sola.
import { useEffect, useState } from 'react';
import { baseApi } from './baseClient';

export type RequestStatus = 'nueva' | 'revision' | 'curso' | 'esperando' | 'hecha' | 'descartada';
export type RequestKind = 'bug' | 'mejora' | 'duda' | 'dato' | 'otro';

export interface ClientRequest {
  id: number;
  kind: RequestKind;
  kindLabel: string;
  area: string | null;
  status: RequestStatus;
  statusLabel: string;
  title: string;
  detail: string | null;
  resolution: string | null;
  doc_url: string | null;
  attachment_url: string | null;
  requested_by: string | null;
  requested_at: string | null;
  eta_date: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface ClientRequestEvent {
  at: string;
  actor: string;
  to_status: RequestStatus | null;
  statusLabel: string | null;
  note: string | null;
}

export interface ClientRequestDetail extends ClientRequest { events: ClientRequestEvent[] }

/** Avisa al menú que cambió algo (para refrescar el globo). */
export const CLIENT_REQUESTS_EVENT = 'lealify:peticiones-cambio';

const BASE = '/client-requests';

export const clientRequestsApi = {
  list: async (estado: 'abiertas' | 'hechas' | 'todas' = 'abiertas'): Promise<ClientRequest[]> =>
    (await baseApi.get<{ data: ClientRequest[] }>(`${BASE}?estado=${estado}`)).data || [],
  get: async (id: number): Promise<ClientRequestDetail> =>
    (await baseApi.get<{ data: ClientRequestDetail }>(`${BASE}/${id}`)).data,
  count: (): Promise<{ esperando: number; abiertas: number; puede?: boolean }> => baseApi.get(`${BASE}/count`),
  create: async (input: { title: string; detail?: string; kind?: RequestKind; attachmentUrl?: string | null }): Promise<ClientRequest> => {
    const r = await baseApi.post<{ data: ClientRequest }>(BASE, input);
    window.dispatchEvent(new Event(CLIENT_REQUESTS_EVENT));
    return r.data;
  },
  reply: async (id: number, text: string): Promise<{ status: RequestStatus; statusLabel: string }> => {
    const r = await baseApi.post<{ status: RequestStatus; statusLabel: string }>(`${BASE}/${id}/responder`, { text });
    window.dispatchEvent(new Event(CLIENT_REQUESTS_EVENT));
    return r;
  },
};

// Sube la captura al uploader público (api.lealify.com) con el token corto de base — el mismo flujo que
// las imágenes de campañas/ImageUpload. La API de peticiones SOLO acepta URLs de api.lealify.com.
export async function uploadRequestScreenshot(file: File): Promise<string> {
  const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
  const { token } = await baseApi.get<{ token: string }>('/uploads/token');
  const fd = new FormData();
  fd.append('image', file, `peticion-${Date.now()}.${ext}`);
  const resp = await fetch('https://api.lealify.com/upload', { method: 'POST', headers: { 'x-upload-token': token }, body: fd });
  const data = await resp.json().catch(() => null);
  if (!resp.ok || !data?.success || !data?.url) throw new Error(data?.error || 'No se pudo subir la captura');
  return data.url.startsWith('http') ? data.url : `https://api.lealify.com${data.url}`;
}

// Globo «esperando tu respuesta» del menú y del botón «Más» (móvil). Cada página monta su propio <Layout>,
// así que el último valor vive fuera del componente: sin esto el globo parpadea en cada clic del menú.
let lastWaiting = 0;
let lastPuede: boolean | null = null;
/** Globo + permiso. `puede` lo decide el SERVIDOR (Carlos 11-oct-2026): dueño/encargado por defecto y
 *  permiso por persona en users.requests_access — cada petición nueva le avisa por WhatsApp.
 *  null = aún no se sabe (no pintar nada). Backend viejo sin `puede` = se permite. */
export function useClientRequestsInfo(): { waiting: number; puede: boolean | null } {
  const [st, setSt] = useState({ waiting: lastWaiting, puede: lastPuede });
  useEffect(() => {
    let alive = true;
    const load = () => clientRequestsApi.count()
      .then((c) => {
        lastPuede = c.puede !== false;
        lastWaiting = lastPuede ? Number(c.esperando) || 0 : 0;
        if (alive) setSt({ waiting: lastWaiting, puede: lastPuede });
      })
      .catch(() => { /* sin globo si falla: es un adorno, no debe romper el menú */ });
    load();
    const iv = window.setInterval(load, 600000);
    window.addEventListener(CLIENT_REQUESTS_EVENT, load);
    return () => { alive = false; window.clearInterval(iv); window.removeEventListener(CLIENT_REQUESTS_EVENT, load); };
  }, []);
  return st;
}
